import React from 'react';
import { Box, Button } from 'bumbag';
import format from 'date-fns/format';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import Loads from 'react-loads';
import type { NavigateFunction } from 'react-router-dom';

import { WithErrorMessage } from '@medipass/react-core';
import { WithLoadingIndicator } from '@medipass/react-core';
import { withRouter } from '@medipass/react-core/router';
import sdk from '@medipass/web-sdk';

import Page from 'legacy/components/account/layout/Page';
import MemberDetailsForm from 'legacy/components/account/members/MemberDetailsForm';
import WithAuthentication from 'legacy/components/WithAuthentication';
import { updateMembersHealthFundAccount } from 'legacy/utils/health-fund-accounts';
import { getKeyFromQueryParams } from 'legacy/utils/router';
type Props = {
  navigate: NavigateFunction;
};
type State = {
  isCreatingHealthFundAccount: boolean;
  isCreatingMedicareAccount: boolean;
  memberId?: string;
};

class EditMember extends React.Component<Props, State> {
  state = {
    isCreatingHealthFundAccount: false,
    isCreatingMedicareAccount: false,
    memberId: getKeyFromQueryParams('id'),
  };
  getMemberResources = async () => {
    const { memberId } = this.state;
    if (!memberId) return;
    const member = await sdk.members.getMember(memberId);
    const ownerMember = await sdk.members.getMember(_get(sdk, 'member._id'));
    let healthFundAccount = null;
    let medicareAccount = null;

    // If the dependant does not have health fund details filled, then see if the owner member does and
    // pre-populate with their health fund info.
    if (member.defaultHealthFundAccountId) {
      healthFundAccount = await sdk.healthFundAccounts.getMembersHealthFundAccount(
        memberId,
        member.defaultHealthFundAccountId,
      );
    } else if (ownerMember.defaultHealthFundAccountId) {
      healthFundAccount = await sdk.healthFundAccounts.getMembersHealthFundAccount(
        ownerMember._id,
        ownerMember.defaultHealthFundAccountId,
      );
      // We don't want to pre-populate the card rank for a dependant.
      healthFundAccount = _omit(healthFundAccount, 'cardRank');
      this.setState({
        isCreatingHealthFundAccount: true,
      });
    }

    // If the dependant does not have Medicare details filled, then see if the owner member does and
    // pre-populate with their Medicare info.
    if (member.medicareHealthFundAccountId) {
      medicareAccount = await sdk.healthFundAccounts.getMembersHealthFundAccount(
        memberId,
        member.medicareHealthFundAccountId,
      );
    } else if (ownerMember.medicareHealthFundAccountId) {
      medicareAccount = await sdk.healthFundAccounts.getMembersHealthFundAccount(
        ownerMember._id,
        ownerMember.medicareHealthFundAccountId,
      );
      // We don't want to pre-populate the card rank for a dependant.
      medicareAccount = _omit(medicareAccount, 'cardRank');
      this.setState({
        isCreatingMedicareAccount: true,
      });
    }

    return {
      member,
      healthFundAccount,
      medicareAccount,
    };
  };
  handleDeleteHealthFundAccount = async ({
    getMemberResources,
    healthFundAccount,
    member,
  }: {
    getMemberResources: (...args: Array<any>) => any;
    healthFundAccount: Record<string, any>;
    member: Record<string, any>;
  }) => {
    await sdk.healthFundAccounts.deleteMembersHealthFundAccount(member._id, healthFundAccount._id);
    getMemberResources();
  };
  handleDeleteMedicareAccount = async ({
    getMemberResources,
    medicareAccount,
    member,
  }: {
    getMemberResources: (...args: Array<any>) => any;
    medicareAccount: Record<string, any>;
    member: Record<string, any>;
  }) => {
    await sdk.healthFundAccounts.deleteMembersHealthFundAccount(member._id, medicareAccount._id);
    getMemberResources();
  };
  deleteMember = async () => {
    const { memberId } = this.state;
    if (!memberId) return;
    await sdk.members.deleteMember(memberId);
    this.goToViewMembers();
  };
  goToViewMembers = () => this.props.navigate('/account/members/');
  handleEditMember = async (data: Record<string, any>) => {
    const { isCreatingHealthFundAccount, isCreatingMedicareAccount, memberId } = this.state;
    if (!memberId) return;
    const dobArray = data.dob.split('/');
    const body = {
      firstName: data.firstName,
      lastName: data.lastName,
      dobString: `${dobArray[2]}-${dobArray[1]}-${dobArray[0]}`,
      mobile: data.mobile,
      email: data.email,
    };
    const member = await sdk.members.updateMember(memberId, body);

    if (_get(data, 'healthFundAccount.cardRank')) {
      const healthFundAccount = data.healthFundAccount;
      await updateMembersHealthFundAccount({
        healthFundAccount,
        memberId: member._id,
        isCreate: isCreatingHealthFundAccount,
      });
    }

    if (_get(data, 'medicareAccount.cardRank')) {
      const medicareAccount = data.medicareAccount;
      await updateMembersHealthFundAccount({
        healthFundAccount: medicareAccount,
        memberId: member._id,
        isCreate: isCreatingMedicareAccount,
      });
    }

    this.goToViewMembers();
    return member;
  };
  handleClickCancel = () => this.goToViewMembers();
  render = () => {
    const { isCreatingHealthFundAccount, isCreatingMedicareAccount } = this.state;
    return (
      <WithAuthentication>
        <Loads loadOnMount load={this.getMemberResources}>
          {/* @ts-expect-error TS(7031): Binding element 'getMemberResources' implicitly ha... Remove this comment to see the full error message */}
          {({ load: getMemberResources, error, response, isError, isLoading, isSuccess }) => {
            const { healthFundAccount, medicareAccount, member } = response || {};

            const isDependant = _get(member, '_id') !== _get(sdk, 'account.ownerMemberId');

            return (
              <Loads load={this.deleteMember}>
                {/* @ts-expect-error TS(7031): Binding element 'handleClickDeleteMember' implicit... Remove this comment to see the full error message */}
                {({ load: handleClickDeleteMember, isLoading: isDeleteMemberLoading }) => (
                  <Page minWidthBreakpoint="mobile" title="Edit member">
                    <WithLoadingIndicator isActive={isLoading}>
                      <WithErrorMessage isActive={isError} description="Unable to load member" error={error}>
                        {isSuccess && (
                          <Box>
                            {/* Don't show the delete button if the member is linked to the account */}
                            {isDependant && (
                              <Box marginBottom="major-2" textAlign="right">
                                <Button
                                  palette="danger"
                                  isLoading={isDeleteMemberLoading}
                                  variant="ghost"
                                  onClick={handleClickDeleteMember}
                                >
                                  <i className="fa fa-sm fa-trash-alt margin-right-5" />
                                  Delete member
                                </Button>
                              </Box>
                            )}
                            <MemberDetailsForm
                              initialValues={{
                                firstName: member.firstName,
                                lastName: member.lastName,
                                dob: member.dobString ? format(member.dobString, 'DD/MM/YYYY') : '',
                                mobile: member.mobile,
                                email: member.email,
                                healthFundAccount: {
                                  healthFundAccountId: _get(healthFundAccount, '_id', ''),
                                  healthFundId: _get(healthFundAccount, 'healthFund._id', ''),
                                  cardIssueNumber: _get(healthFundAccount, 'cardIssueNumber', ''),
                                  cardNumber: _get(healthFundAccount, 'cardNumber', ''),
                                  cardRank: _get(healthFundAccount, 'cardRank', ''),
                                  issueDate: healthFundAccount
                                    ? [
                                        healthFundAccount.issueDay,
                                        healthFundAccount.issueMonth,
                                        healthFundAccount.issueYear,
                                      ]
                                        .filter(x => x)
                                        .join('/')
                                    : '',
                                  membershipNumber: _get(healthFundAccount, 'membershipNumber', ''),
                                },
                                medicareAccount: {
                                  healthFundAccountId: _get(medicareAccount, '_id', ''),
                                  healthFundId: _get(medicareAccount, 'healthFund._id', ''),
                                  cardRank: _get(medicareAccount, 'cardRank', ''),
                                  expiryDate: medicareAccount
                                    ? [medicareAccount.expiryMonth, medicareAccount.expiryYear].filter(x => x).join('/')
                                    : '',
                                  membershipNumber: _get(medicareAccount, 'membershipNumber', ''),
                                },
                              }}
                              isDependant={isDependant}
                              isSelectHealthFundDisabled={Boolean(healthFundAccount) || isDependant}
                              isSelectMedicareDisabled={Boolean(medicareAccount) || isDependant}
                              onClickCancel={this.handleClickCancel}
                              onDeleteHealthFundAccount={() =>
                                this.handleDeleteHealthFundAccount({
                                  getMemberResources,
                                  healthFundAccount,
                                  member,
                                })
                              }
                              onDeleteMedicareAccount={() =>
                                this.handleDeleteMedicareAccount({
                                  getMemberResources,
                                  medicareAccount,
                                  member,
                                })
                              }
                              onSubmit={this.handleEditMember}
                              showDeleteHealthFund={!_isEmpty(healthFundAccount) && !isCreatingHealthFundAccount}
                              showDeleteMedicareDetails={!_isEmpty(medicareAccount) && !isCreatingMedicareAccount}
                            />
                          </Box>
                        )}
                      </WithErrorMessage>
                    </WithLoadingIndicator>
                  </Page>
                )}
              </Loads>
            );
          }}
        </Loads>
      </WithAuthentication>
    );
  };
}

export default withRouter(EditMember);
